import type { AiQuota } from '../../types/ai'

// 「现在能不能调 AI、还能花多少」——本期唯一的共用控件。
//
// 将来任何要调 AI 的动作旁边都会嵌它一份，所以它必须**自己把状态说全**，
// 不依赖上下文文案。五个状态各自的取舍写在下面各分支里。
//
// 数据全部来自服务端的 Ai::QuotaStatus，这里不做任何推算 —— 「约还能跑几次」
// 那个数是按该用户最近 20 次成功调用的真实均价算的，前端算不出来，
// 而拿一个前端估的数字冒充它，会具体得很有说服力却是编的。

const money = (value: number) => {
  if (value === 0) return '$0'
  if (value < 0.01) return '<$0.01'
  return `$${value.toFixed(2)}`
}

// 进度槽与文字互为冗余，故意的：$0.13/$0.50 这种小数字在条上几乎看不出
// 差别，条负责「大概到哪了」，文字负责「到底多少」。
function Meter({ ratio, tone }: { ratio: number; tone: 'sys' | 'warn' }) {
  const width = `${Math.min(100, Math.max(0, ratio * 100))}%`
  return (
    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-200">
      <div
        className={`h-full rounded-full ${tone === 'warn' ? 'bg-amber-600' : 'bg-ai-sys'}`}
        style={{ width }}
      />
    </div>
  )
}

export default function AiQuotaBar({
  quota,
  onAddKey,
}: {
  quota: AiQuota
  onAddKey: () => void
}) {
  const calls = quota.remaining_calls
  const hint = calls === null ? null : `按你最近的用量，大约还能跑 ${calls} 次`

  // 走自己的 key。**不画进度条** —— 自己的 key 没有上限，画一条就是暗示
  // 「这里也有个额度」。右侧换成只读的花费事实（那是用户自己的钱，
  // 他在别处不容易看到）。
  if (quota.state === 'own') {
    return (
      <div className="mb-5 rounded-lg border border-ai-own/30 bg-ai-ownSoft px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-ai-own">✦</span>
          <span className="text-sm font-semibold text-ai-own">AI 可用 · 走你自己的 key</span>
          <span className="rounded-full border border-ai-own/30 bg-white/60 px-2 py-0.5 text-xs text-ai-own">
            {quota.provider} · {quota.model_id}
          </span>
          <span className="flex-1" />
          <span className="text-xs tabular-nums text-gray-600">
            近 30 天 <b>{money(quota.own_spend_30d)}</b> · {quota.own_calls_30d} 次
          </span>
        </div>
        <p className="mt-1.5 text-xs text-gray-600">
          不消耗系统赠送额度，也就没有每日上限 —— 账单直接落在你自己的账号上。
        </p>
      </div>
    )
  }

  // 系统没配 key，用户也没配。文案要说清「不是你的问题」，
  // 并且指向唯一能自救的动作。
  if (quota.state === 'unavailable') {
    return (
      <div className="mb-5 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-gray-500">○</span>
          <span className="text-sm font-semibold text-gray-600">AI 未启用</span>
          <span className="flex-1" />
          <span className="text-xs text-gray-500">无系统赠送额度</span>
        </div>
        <p className="mt-1.5 text-xs text-gray-600">
          这个部署没有配系统 AI key，所以没有赠送额度。
          <b className="text-gray-900">配一个自己的 key 就能直接用</b> —— 不需要等管理员。
        </p>
        <button
          type="button"
          onClick={onAddKey}
          className="mt-2.5 rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700"
        >
          配自己的 key
        </button>
      </div>
    )
  }

  const spent = quota.spent_today ?? 0
  const cap = quota.cap ?? 0
  const ratio = cap > 0 ? spent / cap : 1

  // 额度用尽。**两个出口**，第二个很重要：「复制提示词」那条老路一直都在，
  // 服务端 AI 只是平行通道 —— 额度用完不等于这件事干不了了。
  if (quota.state === 'system_out') {
    return (
      <div className="mb-5 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-amber-700">⊘</span>
          <span className="text-sm font-semibold text-amber-800">今天的系统额度用完了</span>
          <span className="flex-1" />
          <span className="text-xs tabular-nums text-gray-700">
            今天 <b>{money(spent)}</b> / {money(cap)}
          </span>
        </div>
        <Meter ratio={1} tone="warn" />
        <p className="mt-1.5 text-xs text-gray-700">
          明天 00:00（Asia/Shanghai）重置。<b>在那之前有两条路</b>：配一个自己的 key
          直接不受限；或者照原来那样在 /todo 里复制提示词、贴进你自己的 AI 会话 ——
          那条路一直都在。
        </p>
        <button
          type="button"
          onClick={onAddKey}
          className="mt-2.5 rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700"
        >
          配自己的 key
        </button>
      </div>
    )
  }

  // 剩余不足两成 —— 换 warn 色。这不是报错，是**提前一步给出口**，
  // 免得人正干到一半被拦住。
  const low = quota.state === 'system_low'

  return (
    <div
      className={`mb-5 rounded-lg border px-4 py-3 ${
        low ? 'border-amber-300 bg-amber-50' : 'border-ai-sys/25 bg-ai-sysSoft'
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className={low ? 'text-amber-700' : 'text-ai-sys'}>{low ? '◒' : '✦'}</span>
        <span className={`text-sm font-semibold ${low ? 'text-amber-800' : 'text-ai-sys'}`}>
          {low ? '系统额度快用完了' : 'AI 可用 · 用系统赠送额度'}
        </span>
        <span className="rounded-full border border-ai-sys/25 bg-white/60 px-2 py-0.5 text-xs text-ai-sys">
          {quota.provider} · {quota.model_id}
        </span>
        <span className="flex-1" />
        <span className="text-xs tabular-nums text-gray-700">
          今天 <b>{money(spent)}</b> / {money(cap)}
        </span>
      </div>
      <Meter ratio={ratio} tone={low ? 'warn' : 'sys'} />
      <p className="mt-1.5 text-xs text-gray-600">
        {hint ? `${hint} · ` : ''}每天 00:00（Asia/Shanghai）重置
      </p>
      <button
        type="button"
        onClick={onAddKey}
        className={`mt-2.5 rounded-md px-3 py-1 text-xs font-medium ${
          low
            ? 'bg-blue-600 text-white hover:bg-blue-700'
            : 'border border-gray-300 bg-white text-gray-700 hover:border-blue-500 hover:text-blue-600'
        }`}
      >
        {low ? '配自己的 key' : '配一个自己的 key，不受限'}
      </button>
    </div>
  )
}
